import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { Award, ChevronLeft, ChevronRight, Compass, House, Menu, X } from 'lucide-react';
import { CurrentOrgProvider } from '@/lib/current-org';
import { ReadyMark } from '../logo';
import { SiteFooter } from '../footer-links';
import { SidebarContent } from './sidebar';

/*
 * The signed-in app: a left sidebar (My Learning, the current organization,
 * platform admin, the user menu) and the page beside it. On phones the
 * sidebar becomes a drawer, and the everyday learning pages sit in a bottom
 * tab bar. Pages keep the layout's content width (max-w-5xl).
 */

const COLLAPSED_KEY = 'ready.sidebarCollapsed';
const readCollapsed = () => {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
};

export function AppShell() {
  return (
    <CurrentOrgProvider>
      <Shell />
    </CurrentOrgProvider>
  );
}

function Shell() {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [drawer, setDrawer] = useState(false);
  const { pathname } = useLocation();
  // Close the phone drawer whenever the page changes.
  useEffect(() => setDrawer(false), [pathname]);
  const toggleCollapsed = () =>
    setCollapsed((c) => {
      try {
        localStorage.setItem(COLLAPSED_KEY, c ? '0' : '1');
      } catch {
        // Storage blocked: remembered until reload.
      }
      return !c;
    });

  return (
    <div className="flex min-h-dvh">
      <aside
        className={`sticky top-0 z-30 hidden h-dvh shrink-0 border-r border-border bg-card transition-[width] duration-200 md:block ${collapsed ? 'w-16' : 'w-64'}`}
      >
        <SidebarContent collapsed={collapsed} />
        <button
          type="button"
          onClick={toggleCollapsed}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="absolute -right-3 bottom-20 flex size-6 items-center justify-center rounded-full border border-border bg-card text-muted-foreground shadow-sm hover:text-foreground"
        >
          {collapsed ? (
            <ChevronRight className="size-3.5" aria-hidden />
          ) : (
            <ChevronLeft className="size-3.5" aria-hidden />
          )}
        </button>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Phones: a slim bar with the brand. */}
        <header className="sticky top-0 z-20 flex h-14 items-center border-b border-border bg-background/95 px-4 backdrop-blur md:hidden">
          <Link to="/home" className="-my-2 flex items-center gap-2 py-2 font-semibold">
            <ReadyMark className="size-6 text-primary" />
            Nirvik Ready
          </Link>
        </header>
        <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-6 sm:px-6 sm:py-10">
          <Outlet />
        </main>
        <div className="pb-16 md:pb-0">
          <SiteFooter />
        </div>
      </div>

      <BottomNav onMenu={() => setDrawer(true)} />
      <Drawer open={drawer} onClose={() => setDrawer(false)} />
    </div>
  );
}

/** Phones: the everyday pages, plus Menu for everything else. */
function BottomNav({ onMenu }: { onMenu: () => void }) {
  const tab = ({ isActive }: { isActive: boolean }) =>
    `flex flex-col items-center justify-center gap-0.5 text-xs ${isActive ? 'font-medium text-primary-strong' : 'text-muted-foreground'}`;
  return (
    <nav
      aria-label="Main"
      className="fixed inset-x-0 bottom-0 z-30 grid h-16 grid-cols-4 border-t border-border bg-card pb-[env(safe-area-inset-bottom)] md:hidden"
    >
      <NavLink to="/home" className={tab}>
        <House className="size-5" aria-hidden /> Home
      </NavLink>
      <NavLink to="/learn" className={tab}>
        <Compass className="size-5" aria-hidden /> Explore
      </NavLink>
      <NavLink to="/certificates" className={tab}>
        <Award className="size-5" aria-hidden /> Certificates
      </NavLink>
      <button type="button" onClick={onMenu} className={tab({ isActive: false })}>
        <Menu className="size-5" aria-hidden /> Menu
      </button>
    </nav>
  );
}

/** Phones: the whole sidebar, from the left. */
function Drawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    document.documentElement.classList.add('overflow-hidden');
    panel.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.documentElement.classList.remove('overflow-hidden');
    };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true" aria-label="Menu">
      <button
        type="button"
        aria-label="Close menu"
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      <div
        ref={panel}
        tabIndex={-1}
        className="absolute inset-y-0 left-0 flex w-[min(20rem,85vw)] flex-col bg-card shadow-xl outline-none"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close menu"
          className="absolute right-2 top-2.5 z-10 flex size-9 items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
        >
          <X className="size-5" aria-hidden />
        </button>
        <SidebarContent collapsed={false} onNavigate={onClose} />
      </div>
    </div>
  );
}
